import { useEffect } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"

import ChangePasscode from "./parentPasscodeSteps/ChangePasscode"
import ConfirmChangePasscode from "./parentPasscodeSteps/ConfirmChangePasscode"
import ConfirmDeletePasscode from "./parentPasscodeSteps/ConfirmDeletePasscode"
import ConfirmSetPasscode from "./parentPasscodeSteps/ConfirmSetPasscode"
import EditPasscode from "./parentPasscodeSteps/EditPasscode"
import EnterPasscode from "./parentPasscodeSteps/EnterPasscode"
import Requirements from "./parentPasscodeSteps/Requirements"
import SetPasscode from "./parentPasscodeSteps/SetPasscode"
import { useParentPasscodeModalStore } from "./useParentPasscodeModalStore"
import { CloseButton } from "../../../../components/closeButton/CloseButton"
import TransitionWrapper from "../../../../components/coaching/TransitionWrapper"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { useUserQuery } from "../../../../networking/queries"
import {
  useEditParentPanelPasscodeMutation,
  useEditPasscodeMutation,
} from "../../../../networking/rewards/mutations"
import { ParentPasscodeStep } from "../../../../types"

const ParentPasscodeModal = () => {
  const {
    isOpen,
    onClose,
    passcode,
    step,
    setStep,
    hasParentAppRequirement,
    setHasParentAppRequirement,
    hasParentPanelRequirement,
    setHasParentPanelRequirement,
  } = useParentPasscodeModalStore()
  const { user } = useUserQuery()
  const { t } = useTranslation()

  const editPasscodeMutation = useEditPasscodeMutation()
  const editParentPanelPasscodeMutation = useEditParentPanelPasscodeMutation()

  const onSubmit = () => {
    if (!user) return
    const isChangingPasscode =
      user.account_passcode !== null && hasParentAppRequirement
    const isChangingParentPanelPasscode =
      user.parent_panel_passcode !== null && hasParentPanelRequirement

    if (hasParentAppRequirement) {
      editPasscodeMutation.mutate({
        userId: user.id,
        accountPasscode: passcode,
      })
    }
    if (hasParentPanelRequirement) {
      editParentPanelPasscodeMutation.mutate({
        userId: user.id,
        parentPanelPasscode: passcode,
      })
    }
    onClose()
    if (isChangingPasscode || isChangingParentPanelPasscode)
      toast.success(t("PPC_TOAST_CHANGED"))
    else toast.success(t("PPC_TOAST_SET"))
  }

  const onDelete = () => {
    if (!user?.id) return
    editPasscodeMutation.mutate({
      userId: user.id,
      accountPasscode: null,
    })
    editParentPanelPasscodeMutation.mutate({
      userId: user.id,
      parentPanelPasscode: null,
    })
    onClose()
    toast.success(t("PPC_TOAST_TURNED_OFF"))
  }

  useEffect(() => {
    if (!isOpen || !user) return
    const hasAccountPasscode = user.account_passcode !== null
    const hasParentPanelPasscode = user.parent_panel_passcode !== null
    if (!hasAccountPasscode && !hasParentPanelPasscode) {
      setStep(ParentPasscodeStep.SET_PASSCODE)
    } else {
      setStep(ParentPasscodeStep.ENTER_PASSCODE)
    }
    setHasParentAppRequirement(hasAccountPasscode)
    setHasParentPanelRequirement(hasParentPanelPasscode)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1000}
    >
      <div className="w-full p-4 pt-6">
        <CloseButton onClick={onClose} />
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.ENTER_PASSCODE}
        >
          <EnterPasscode />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.SET_PASSCODE}
        >
          <SetPasscode />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.CONFIRM_SET_PASSCODE}
        >
          <ConfirmSetPasscode />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.CHANGE_PASSCODE}
        >
          <ChangePasscode />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.CONFIRM_CHANGE_PASSCODE}
        >
          <ConfirmChangePasscode onSubmit={onSubmit} />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.EDIT_PASSCODE}
        >
          <EditPasscode />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.DELETE_PASSCODE}
        >
          <ConfirmDeletePasscode onDelete={onDelete} />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ParentPasscodeStep.REQUIREMENTS}
        >
          <Requirements onSubmit={onSubmit} />
        </TransitionWrapper>
      </div>
    </SlideUpModal>
  )
}

export default ParentPasscodeModal
